import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { AlertTriangle, Trash2, LogIn } from "lucide-react";
import authService from "../Services/authService";
import useCoinStore from "../stores/coinStore";
import LoginModal from "../Components/LoginModal";
import AccountUpgradeModal from "../Components/AccountUpgradeModal";
import { GoogleAuthProvider, signInWithPopup, EmailAuthProvider, reauthenticateWithCredential } from "firebase/auth";

const DeleteAccount = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [accountType, setAccountType] = useState(null);
  const [step, setStep] = useState("warning");
  const [confirmText, setConfirmText] = useState("");
  const [agreed, setAgreed] = useState(false);
  const [password, setPassword] = useState("");
  const [isReauthenticated, setIsReauthenticated] = useState(false);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [showSignUpModal, setShowSignUpModal] = useState(false);

  useEffect(() => {
    checkAuthStatus();
  }, []);

  const checkAuthStatus = () => {
    const user = authService.auth.currentUser;
    
    if (!user) {
      setAccountType("none");
      return;
    }

    if (user.isAnonymous) {
      setAccountType("anonymous");
      return;
    }

    const googleProvider = user.providerData.find(p => p.providerId === "google.com");
    if (googleProvider) {
      setAccountType("google");
      return;
    }

    const emailProvider = user.providerData.find(p => p.providerId === "password");
    if (emailProvider) {
      setAccountType("email");
      return;
    }

    setAccountType("unknown");
  };

  const handleReauthGoogle = async () => {
    setLoading(true);
    setError("");

    try {
      const provider = new GoogleAuthProvider();
      await signInWithPopup(authService.auth, provider);
      setIsReauthenticated(true);
      setStep("confirm");
    } catch (err) {
      console.error("Re-authentication error:", err);
      setError("Failed to verify your identity. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleReauthEmail = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    try {
      const user = authService.auth.currentUser;
      const credential = EmailAuthProvider.credential(user.email, password);
      await reauthenticateWithCredential(user, credential);
      setIsReauthenticated(true);
      setStep("confirm");
    } catch (err) {
      console.error("Re-authentication error:", err);
      setError("Incorrect password. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleLoginSuccess = (result) => {
    console.log("✅ Login successful, checking account status");
    setShowLoginModal(false);
    setShowSignUpModal(false);
    checkAuthStatus();
    setStep("warning");
  };

  const handleDeleteAccount = async () => {
    if (!agreed || confirmText !== "DELETE") {
      setError("Please complete all confirmation steps");
      return;
    }

    if (!isReauthenticated) {
      setError("Please re-authenticate before deleting your account");
      return;
    }

    setLoading(true);
    setError("");

    try {
      useCoinStore.getState().resetStore();
      
      const result = await authService.deleteAccount();
      
      if (result.success) {
        setStep("deleted");
        setTimeout(() => {
          navigate("/");
        }, 3000);
      } else {
        setError(result.message || "Failed to delete account");
        setLoading(false);
      }
    } catch (err) {
      console.error("Delete account error:", err);
      setError("An error occurred while deleting your account");
      setLoading(false);
    }
  };

  if (accountType === "none" || accountType === "anonymous") {
    return (
      <>
        <div className="min-h-screen bg-gradient-to-br from-red-50 to-orange-50 flex items-center justify-center p-6">
          <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md w-full border border-gray-200">
            <div className="flex justify-center mb-6">
              <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center">
                <LogIn className="w-8 h-8 text-orange-600" />
              </div>
            </div>
            
            <h1 className="text-2xl font-bold text-gray-800 text-center mb-4">
              Sign In Required
            </h1>
            
            <p className="text-gray-600 text-center mb-6">
              {accountType === "anonymous" 
                ? "You're using an anonymous account. To delete your account data, please sign in to an existing account first."
                : "Please sign in to the account you want to delete."
              }
            </p>

            <div className="space-y-3">
              <button
                onClick={() => setShowLoginModal(true)}
                className="w-full bg-red-600 text-white py-3 rounded-lg hover:bg-red-700 transition-colors font-semibold"
              >
                Sign In to Existing Account
              </button>
              <button
                onClick={() => navigate("/")}
                className="w-full bg-gray-200 text-gray-700 py-3 rounded-lg hover:bg-gray-300 transition-colors font-semibold"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>

        <LoginModal
          isOpen={showLoginModal}
          onClose={() => setShowLoginModal(false)}
          onSuccess={handleLoginSuccess}
          onSwitchToSignUp={() => {
            setShowLoginModal(false);
            setShowSignUpModal(true);
          }}
        />
        <AccountUpgradeModal
          isOpen={showSignUpModal}
          onClose={() => setShowSignUpModal(false)}
          onSuccess={handleLoginSuccess}
          onSwitchToSignIn={() => {
            setShowSignUpModal(false);
            setShowLoginModal(true);
          }}
        />
      </>
    );
  }

  if (step === "deleted") {
    return (
      <div className="min-h-screen bg-gradient-to-br from-green-50 to-blue-50 flex items-center justify-center p-6">
        <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md w-full border border-gray-200 text-center">
          <div className="flex justify-center mb-6">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center">
              <Trash2 className="w-8 h-8 text-green-600" />
            </div>
          </div>
          
          <h1 className="text-2xl font-bold text-gray-800 mb-4">
            Account Deleted
          </h1>
          
          <p className="text-gray-600 mb-6">
            Your account and all associated data have been permanently deleted.
          </p>

          <p className="text-sm text-gray-500">
            Redirecting to home page in 3 seconds...
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-red-50 to-orange-50 flex items-center justify-center p-6">
      <div className="bg-white rounded-2xl shadow-xl p-8 max-w-2xl w-full border border-gray-200">
        <div className="flex justify-center mb-6">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center">
            <AlertTriangle className="w-8 h-8 text-red-600" />
          </div>
        </div>

        <h1 className="text-3xl font-bold text-gray-800 text-center mb-2">
          Delete Your Account
        </h1>
        <p className="text-gray-600 text-center mb-2">
          NameIt!
        </p>
        <p className="text-gray-500 text-center text-sm mb-8">
          Account Deletion Request
        </p>

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-6">
            {error}
          </div>
        )}

        {step === "warning" && (
          <div className="space-y-6">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 mb-6">
              <h2 className="font-bold text-blue-900 mb-3 text-lg">
                How to Delete Your Account
              </h2>
              <ol className="space-y-2 text-blue-800 list-decimal list-inside">
                <li>Review what data will be deleted below</li>
                <li>Enter your password to verify your identity</li>
                <li>Complete the final confirmation steps</li>
                <li>Your account will be deleted immediately</li>
              </ol>
            </div>

            <div className="bg-red-50 border border-red-200 rounded-lg p-6">
              <h2 className="font-bold text-red-800 mb-3 text-lg">
                Data That Will Be Deleted Immediately:
              </h2>
              <ul className="space-y-2 text-red-700">
                <li className="flex items-start">
                  <span className="mr-2">•</span>
                  <span>Your entire coin balance and purchase history</span>
                </li>
                <li className="flex items-start">
                  <span className="mr-2">•</span>
                  <span>All rooms you've created (will be permanently deleted)</span>
                </li>
                <li className="flex items-start">
                  <span className="mr-2">•</span>
                  <span>Your participation in other users' rooms</span>
                </li>
                <li className="flex items-start">
                  <span className="mr-2">•</span>
                  <span>All saved preferences and settings</span>
                </li>
                <li className="flex items-start">
                  <span className="mr-2">•</span>
                  <span>Your user profile and account information</span>
                </li>
              </ul>
            </div>

            <div className="bg-gray-50 border border-gray-200 rounded-lg p-6">
              <h2 className="font-bold text-gray-800 mb-2 text-lg">
                Data Retention Policy:
              </h2>
              <p className="text-gray-700">
                <strong>All data is deleted immediately.</strong> There is no retention period. Once you confirm deletion, your account and all associated data are permanently removed from our servers and cannot be recovered.
              </p>
            </div>

            <div className="text-center">
              <p className="text-gray-700 font-semibold mb-4">
                To proceed, you must re-authenticate your account
              </p>

              {accountType === "google" ? (
                <button
                  onClick={handleReauthGoogle}
                  disabled={loading}
                  className="bg-white border-2 border-gray-300 text-gray-700 px-6 py-3 rounded-lg hover:bg-gray-50 transition-colors font-semibold inline-flex items-center"
                >
                  {loading ? (
                    "Verifying..."
                  ) : (
                    <>
                      <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
                        <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                        <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                        <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                        <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                      </svg>
                      Continue with Google
                    </>
                  )}
                </button>
              ) : (
                <form onSubmit={handleReauthEmail} className="max-w-sm mx-auto">
                  <input
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Enter your password"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg mb-4 focus:ring-2 focus:ring-red-500 focus:border-red-500 outline-none"
                    required
                  />
                  <button
                    type="submit"
                    disabled={loading}
                    className="w-full bg-red-600 text-white py-3 rounded-lg hover:bg-red-700 transition-colors font-semibold"
                  >
                    {loading ? "Verifying..." : "Verify Password"}
                  </button>
                </form>
              )}
            </div>

            <div className="text-center pt-4">
              <button
                onClick={() => navigate("/")}
                className="text-gray-600 hover:text-gray-800 font-medium"
              >
                Cancel and Go Back
              </button>
            </div>
          </div>
        )}

        {step === "confirm" && (
          <div className="space-y-6">
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
              <p className="text-yellow-800 font-semibold text-center">
                Final confirmation required
              </p>
            </div>

            <div className="space-y-4">
              <label className="flex items-start space-x-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={agreed}
                  onChange={(e) => setAgreed(e.target.checked)}
                  className="mt-1 w-5 h-5 text-red-600 rounded focus:ring-2 focus:ring-red-500"
                />
                <span className="text-gray-700">
                  I understand this action is permanent and cannot be undone. All my data will be deleted immediately.
                </span>
              </label>

              <div>
                <label className="block text-gray-700 font-semibold mb-2">
                  Type <span className="text-red-600">DELETE</span> to confirm:
                </label>
                <input
                  type="text"
                  value={confirmText}
                  onChange={(e) => setConfirmText(e.target.value)}
                  placeholder="DELETE"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 outline-none font-mono"
                />
              </div>
            </div>

            <div className="flex space-x-4 pt-4">
              <button
                onClick={() => navigate("/account")}
                className="flex-1 bg-gray-200 text-gray-700 py-3 rounded-lg hover:bg-gray-300 transition-colors font-semibold"
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteAccount}
                disabled={loading || !agreed || confirmText !== "DELETE"}
                className="flex-1 bg-red-600 text-white py-3 rounded-lg hover:bg-red-700 transition-colors font-semibold disabled:bg-gray-300 disabled:cursor-not-allowed"
              >
                {loading ? "Deleting..." : "Delete My Account"}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default DeleteAccount;